import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth, dataStore } from './app.component';
import { inject } from '@angular/core';
import { PermissionsService } from './services/permissions.service';
import { Role } from './enums';
import { NavigationContext } from './navigation-helper';

export const redirectUnauthorized: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  const router: Router = inject(Router);
  const permissionsService = inject(PermissionsService);
  const authorizedRights: Role[] = route.data.authorizedRights ?? [];

  const authUser: any = await new Promise((resolve) => {
    auth.onAuthStateChanged((user) => resolve(user));
  });

  if (!authUser?.uid) {
    return router.parseUrl('login');
  }

  const rootUser = dataStore?.rootUser ?? (await getDoc(doc(db, `users/${authUser.uid}`))).data();

  if (rootUser) {
    // Super User check (rights === 'admin')
    if (rootUser.rights && rootUser.rights === 'admin') {
      return true;
    }

    const orgUser =
      dataStore?.orgUser ??
      (
        await getDoc(doc(db, `organisations/${rootUser.selectedOrgId}/users/${authUser.uid}`))
      ).data();

    if (orgUser && orgUser.roles) {
      const roles: Role[] = orgUser.roles;
      const navigationContext = await getNavigationContext(rootUser);

      // Check if any of the user's roles are in the authorized rights list
      const hasAccess = roles.some((role) => authorizedRights.includes(role));

      if (hasAccess && isRouteAvailable(route, navigationContext)) {
        return true;
      } else if (
        route.routeConfig?.path === 'employees' &&
        (await hasEmployeeListAccess(
          roles,
          dataStore?.selectedOrganisation?.id ?? rootUser.selectedOrgId,
        ))
      ) {
        return true;
      }

      return router.parseUrl(
        permissionsService.getDefaultRoute(authUser.uid, roles, navigationContext),
      );
    } else {
      return router.parseUrl('selectingOrganisation');
    }
  }
  return false;
};

async function hasEmployeeListAccess(
  roles: Role[],
  organisationId: string | undefined,
): Promise<boolean> {
  if (roles.includes(Role.Administrator)) {
    return true;
  }

  const isInternalStaff = roles.some((role) =>
    [Role.Planner, Role.RidePlanner, Role.Driver, Role.CoDriver, Role.Other].includes(role),
  );
  if (!isInternalStaff || !organisationId) {
    return false;
  }

  const organisation = await getDoc(doc(db, `organisations/${organisationId}`));
  return organisation.data()?.paidFunctionalities?.usesEmployeeListForStaff === true;
}

export const redirectLoggedIn: CanActivateFn = async () => {
  const router: Router = inject(Router);
  const permissionsService = inject(PermissionsService);
  const authUser: any = await new Promise((resolve) => {
    auth.onAuthStateChanged((user) => resolve(user));
  });

  if (authUser) {
    const roles = dataStore?.orgUser?.roles;
    const navigationContext = await getNavigationContext(dataStore?.rootUser);
    return router.parseUrl(
      permissionsService.getDefaultRoute(authUser.uid, roles, navigationContext),
    );
  }
  return true;
};

async function getNavigationContext(
  rootUser: any,
): Promise<Omit<NavigationContext, 'roles' | 'userId'>> {
  const organisationId = dataStore.selectedOrganisation?.id ?? rootUser?.selectedOrgId;
  let paidFunctionalities = dataStore.paidFunctionalities ?? {};
  let generalSettings: {
    ignoreDriverStatus?: boolean;
    coDriverEnabled?: boolean;
  } = dataStore.generalSettings ?? {};

  if (
    organisationId &&
    (paidFunctionalities.usesDriverPlanning === undefined ||
      paidFunctionalities.usesEmployeeListForStaff === undefined)
  ) {
    try {
      const organisation = (await getDoc(doc(db, `organisations/${organisationId}`))).data();
      paidFunctionalities = {
        ...organisation?.paidFunctionalities,
        ...paidFunctionalities,
      };
    } catch {
      paidFunctionalities = {};
    }
  }

  if (
    organisationId &&
    (generalSettings.ignoreDriverStatus === undefined ||
      generalSettings.coDriverEnabled === undefined)
  ) {
    try {
      generalSettings = {
        ...(await getDoc(doc(db, `organisations/${organisationId}/settings/general`))).data(),
        ...generalSettings,
      };
    } catch {
      generalSettings = {};
    }
  }

  return {
    rootRights: rootUser?.rights,
    usesDriverPlanning: paidFunctionalities.usesDriverPlanning === true,
    usesEmployeeListForStaff: paidFunctionalities.usesEmployeeListForStaff === true,
    ignoreDriverStatus: generalSettings.ignoreDriverStatus === true,
    coDriverEnabled: generalSettings.coDriverEnabled,
  };
}

function isRouteAvailable(
  route: ActivatedRouteSnapshot,
  context: Omit<NavigationContext, 'roles' | 'userId'>,
): boolean {
  const routePath = route.routeConfig?.path ?? '';
  const isRoosterRoute = routePath === 'schedule' || routePath.startsWith('schedule/');
  const isSchedulePlanningRoute = routePath.startsWith('planning/');

  if (!isRoosterRoute && !isSchedulePlanningRoute) {
    return true;
  }

  if (context.usesDriverPlanning !== true || context.ignoreDriverStatus === true) {
    return false;
  }

  return route.params?.['type'] !== 'co-driver' || context.coDriverEnabled !== false;
}

export const redirectToLogin: CanActivateFn = async () => {
  const router: Router = inject(Router);
  const authUser: any = await new Promise((resolve) => {
    auth.onAuthStateChanged((user) => resolve(user));
  });
  return authUser ? true : router.parseUrl('login');
};
